/**
 * Service API Types
 * These interfaces match the Go backend API responses
 */

export interface IServiceProject {
  id: number;
  code: string;
  name: string;
  is_archived: boolean;
}

export interface IServiceEmployeeSettings {
  is_bookable: boolean;
}

export interface IServiceEmployee {
  id: number;
  email: string;
  department: string | null;
  department_name?: string | null;
  given_name: string;
  surname: string;
  name: string;
  is_archived: boolean;
  settings: IServiceEmployeeSettings;
}

export interface IServiceActivity {
  id: number;
  code: string;
  name: string;
}

export interface IServiceCustomer {
  id: number;
  name: string;
}

export interface IUserState {
  id: number;
  email: string;
  name: string;
  role_level: number;
  organization_settings: IOrganizationSettings;
}

export interface IOrganizationSettings {
  hours_in_day: number;
  disable_fravaer: boolean;
  /** Include public holidays in capacity calculation */
  include_holidays: boolean;
  /** Include weekends in capacity calculation */
  include_weekends: boolean;
}

/**
 * Menu item for accessible pages
 */
export interface IMenuItem {
  id: number;
  name: string;
  route: string;
}

// ============ ADMIN TYPES ============

/**
 * Superuser from API
 */
export interface ISuperuser {
  id: number;
  user_id: number;
  name: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

/**
 * Request payload for creating a superuser
 * Backend expects email field (see booking/api/commonInput.go:SuperUsersInput)
 */
export interface ISuperuserCreateRequest {
  email: string;
}

/**
 * User for autocomplete selection
 */
export interface IUser {
  id: number;
  name: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

// Role level constants
export const ROLE_ADMIN = 1;
export const ROLE_MANAGER = 2;
export const ROLE_SALES = 3;
export const ROLE_EMPLOYEE = 4;

// ============ SUPERADMIN TYPES ============

/**
 * Organization from superadmin API
 */
export interface IOrganization {
  id: number;
  tenant_id: string;
  tenant_name: string;
  service_id: string;
  is_active: boolean;
}

/**
 * Service-specific credentials for organization
 */
export interface IServiceData {
  token?: string;
  app_key?: string;
  client_key?: string;
  subscription_key?: string;
}

/**
 * Request payload for creating/updating an organization
 */
export interface IOrganizationInput {
  name: string;
  tenant_id: string;
  service_id: string;
  service_data: IServiceData;
}

/**
 * Request payload for activating/deactivating an organization
 */
export interface IOrganizationActivationInput {
  id: number;
  is_active: boolean;
}

/**
 * Endpoint group from API
 */
export interface IEndpointGroup {
  id: number;
  name: string;
  description: string;
}

/**
 * Organization's endpoint group access
 */
export interface IOrgEndpointGroups {
  organization_id: number;
  endpoint_groups: IEndpointGroup[];
}

/**
 * Request payload for adding/removing endpoint group access
 */
export interface IEndpointGroupAccessInput {
  organization_id: number;
  endpoint_group_id: number;
}

/**
 * Page from API (frontend routes)
 */
export interface IPage {
  id: number;
  name: string;
  route: string;
}

/**
 * Organization's page access
 */
export interface IOrgPages {
  organization_id: number;
  pages: IPage[];
}

/**
 * Request payload for adding/removing page access
 */
export interface IPageAccessInput {
  organization_id: number;
  page_id: number;
}

/**
 * Service ID constants for organization services
 */
export const ServiceId = {
  XLEDGER: "1",
  POWEROFFICE: "2",
} as const;

export type ServiceId = (typeof ServiceId)[keyof typeof ServiceId];

/**
 * Get service display name from service ID
 */
export function getServiceName(serviceId: string): string {
  switch (serviceId) {
    case ServiceId.XLEDGER:
      return "Xledger";
    case ServiceId.POWEROFFICE:
      return "PowerOffice";
    default:
      return "Ukjent";
  }
}

// ============ BOOKING TYPES ============

/**
 * Booking type constants matching Go backend
 */
export const BookingType = {
  HARD_BOOKING: 1,
  SOFT_BOOKING: 2,
  ABSENCE: 3,
} as const;

export type BookingType = (typeof BookingType)[keyof typeof BookingType];

/**
 * Raw booking from API (dates as strings)
 */
export interface IRawBooking {
  id: number;
  date: string;
  project: IServiceProject | null;
  employee: IServiceEmployee | null;
  activity: IServiceActivity;
  b_type: BookingType;
  hours: number;
  created_at: string;
  updated_at: string;
  booked_by: IServiceEmployee;
  customer: IServiceCustomer | null;
}

/**
 * Parsed booking with Date objects
 */
export interface IBooking {
  id: number;
  date: Date;
  project: IServiceProject | null;
  employee: IServiceEmployee | null;
  activity: IServiceActivity;
  b_type: BookingType;
  hours: number;
  created_at: Date;
  updated_at: Date;
  booked_by: IServiceEmployee;
  customer: IServiceCustomer | null;
}

/**
 * Booking payload for create/update
 */
export interface IPostBooking {
  id: number | null;
  date: Date | string;
  project: number | null;
  employee: number | null;
  activity: number;
  b_type: BookingType;
  hours: number;
  customer: number | null;
}

/**
 * Aggregated booking data for year view
 */
export interface IBookingYear {
  date: string | Date;
  hours: number;
  b_type: number;
  customer: IServiceCustomer | null;
}

/**
 * Overbooking validation result
 */
export interface IOverbooking {
  employee: IServiceEmployee;
  date: Date | string;
  hours_over: number;
  input_hours: number;
}

/**
 * API response with pagination
 */
export interface IBookingResponse {
  results: IRawBooking[];
  next: string | null;
}

/**
 * Booking filters for API queries
 */
export interface IBookingFilters {
  employees: IServiceEmployee[];
  projects: IServiceProject[];
  customers: IServiceCustomer[];
  dateRange: {
    start: Date;
    end: Date;
  };
  bookingType: BookingType;
}

/**
 * View mode for booking display
 */
export type BookingViewMode = "week" | "month" | "year";

/**
 * Selection period types
 */
export const SelectionPeriod = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
  YEAR_WINDOW: "year-window",
} as const;

export type SelectionPeriod =
  (typeof SelectionPeriod)[keyof typeof SelectionPeriod];
